// @flow
import './SignatureQuestionRender.css'

import { Signature as SignatureIcon, TextT } from '@phosphor-icons/react'
import { Button } from 'antd'
import isNil from 'lodash/isNil'
import * as React from 'react'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import Flex from 'ui/atoms/Flex'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import TextField from 'ui/molecules/TextField'
import getSignedOnDateMessage from 'utils/signatureQuestion'
import { ZentakeContext } from 'utils/ZentakeContext'

import cn from '@/utils/tailwind/cn'

import { ButtonGroup } from './atoms/ButtonGroup'
import Icons from './Icons'
import PrimaryButton from './PrimaryButton'
import Signature from './Signature'
import { QuestionTitle } from './Text'

type ImageAnswer = {|
  image: ?string,
  text: ?string,
  untrimmedImage?: ?string,
  id?: any,
|}

type Props = {|
  answer: ?{
    id?: any,
    image: ?string,
    text: ?string,
    created_at?: string,
    untrimmedImage?: ?string,
  },
  questionId?: string,
  displayOnly?: boolean,
  errorMessage?: string,
  forceClearButton?: boolean,
  forceShowPad?: boolean,
  hasError?: boolean,
  hideConfirmButton?: boolean,
  hideConsentText?: boolean,
  hideInitialAll?: boolean,
  hideLabel?: boolean,
  initials?: boolean,
  isOfficeUse?: boolean,
  isRequired?: boolean,
  label?: string,
  onConfirm: (answer: ImageAnswer) => Promise<void> | void,
  onInitialAll?: (ImageAnswer) => Promise<void>,
  onReset: () => void,
  readonly?: boolean,
  showConsent?: boolean,
  showNotAllowedCursor?: boolean,
  smallSignature?: boolean,
  subtype?: string,
  readOnlyClickAction?: () => ?{ image: string, untrimmedImage: string },
  showReadOnlyPlaceholder?: boolean,
  forceSignatureLine?: boolean,
  memorizedImage?: string,
  noModal?: boolean,
  onCancel?: () => void,
  isVisible?: boolean,
|}

export function SignatureQuestionRender({
  questionId,
  answer,
  displayOnly,
  errorMessage,
  forceClearButton,
  forceShowPad,
  hasError,
  hideConfirmButton,
  hideConsentText,
  hideInitialAll,
  hideLabel,
  initials,
  isOfficeUse,
  isRequired,
  label,
  onConfirm,
  onInitialAll,
  onReset,
  readonly,
  showConsent,
  showNotAllowedCursor,
  smallSignature,
  subtype,
  readOnlyClickAction,
  showReadOnlyPlaceholder,
  forceSignatureLine,
  memorizedImage,
  noModal,
  onCancel,
  isVisible,
}: Props): React.Node {
  const { teamConfig } = React.useContext(ZentakeContext)
  const image = answer?.image ?? null
  const text = answer?.text ?? ''

  const [isConfirmed, setConfirmed] = React.useState(
    Boolean(answer?.created_at)
  )
  const [isConfirming, setIsConfirming] = React.useState(false)
  const [localImage, setLocalImage] = React.useState()
  const [localUntrimmedImage, setLocalUntrimmedImage] = React.useState()
  const [localText, setLocalText] = React.useState()
  const [showPad, setShowPad] = React.useState(false)
  const [showModal, setShowModal] = React.useState(false)

  const isDraw = subtype === 'draw'

  let modalTitle = 'Draw your signature'
  if (!isDraw) modalTitle = 'Type your signature'

  React.useEffect(() => {
    setLocalImage(image)
  }, [image])

  React.useEffect(() => {
    if ((memorizedImage || image) && !text) {
      setShowPad(true)
    }
  }, [memorizedImage, image, text])

  React.useEffect(() => {
    setLocalText(text)
  }, [text])

  React.useEffect(() => {
    if (readonly) return

    if (subtype === 'type') {
      setShowPad(false)
      return
    }

    if (subtype === 'draw') {
      setShowPad(true)
    }
  }, [readonly, subtype])

  const handleImageChange = React.useCallback((signatures) => {
    const [image, untrimmedImage] = signatures
    setLocalImage(image)
    setLocalUntrimmedImage(untrimmedImage)
  }, [])

  const onResetHandler = React.useCallback(() => {
    setLocalText('')
    setLocalImage(null)
    setLocalUntrimmedImage(null)
    onReset()
    setConfirmed(false)
  }, [onReset])

  const handleTextChange = React.useCallback((text) => {
    setLocalText(text)
  }, [])

  const switchToTextMode = React.useCallback(() => {
    if (showPad) {
      setShowPad(false)
      if (!isNil(answer)) {
        onResetHandler()
      }
    }
  }, [answer, onResetHandler, showPad])

  const switchToPadMode = React.useCallback(() => {
    if (!showPad) {
      setShowPad(true)

      if (!isNil(answer)) {
        onResetHandler()
      }
    }
  }, [answer, onResetHandler, showPad])

  const answerToSave = React.useMemo(() => {
    return showPad
      ? {
          id: answer?.id || answer?.question,
          image: localImage,
          text: null,
          untrimmedImage: localUntrimmedImage,
        }
      : {
          id: answer?.id || answer?.question,
          image: null,
          text: localText,
          untrimmedImage: null,
        }
  }, [
    answer?.id,
    answer?.question,
    localImage,
    localText,
    localUntrimmedImage,
    showPad,
  ])

  const onConfirmHandler = React.useCallback(async () => {
    try {
      setIsConfirming(true)

      await onConfirm(answerToSave)

      setConfirmed(true)
      setShowModal(false)
    } catch (error) {
    } finally {
      setIsConfirming(false)
    }
  }, [onConfirm, answerToSave])

  const shouldDisplayTypeControls =
    (!readonly && (subtype === 'all' || subtype === 'signature')) ||
    forceShowPad

  const onHandleTab = (value: string) => {
    if (value === 'draw') {
      switchToPadMode()
    }
    if (value === 'text') {
      switchToTextMode()
    }
    onResetHandler()
  }

  const tabOption = showPad ? 'draw' : 'text'

  const SignaturePad = (
    <Signature
      answer={answer}
      disabled={displayOnly || readonly || isConfirmed || showPad !== true}
      displayOnly={displayOnly}
      forceClearButton={forceClearButton}
      forceSignatureLine={showReadOnlyPlaceholder || forceSignatureLine}
      image={localImage}
      isConfirmed={isConfirmed}
      key={`signature-${showPad}-${showModal}`}
      onChange={handleImageChange}
      onReset={onResetHandler}
      placeholder={showReadOnlyPlaceholder ? 'CLICK TO SIGN' : ''}
      readonly={displayOnly || readonly}
      small={smallSignature}
    />
  )
  const SingleLine = (
    <div className="relative h-60 w-full rounded border border-gray-50 bg-gray-100">
      <div className="absolute bottom-6 left-6 right-6">
        <TextField
          grayOutWhenDisabled
          className="absolute bottom-0 h-full w-full rounded-b-none border !border-l-0 !border-r-0 !border-t-0 !border-black !bg-gray-100 text-xl focus:border-black focus:shadow-none focus:outline-none sm:text-xl"
          disabled={
            displayOnly === true || readonly || isConfirmed || showPad === true
          }
          hasError={hasError}
          inputId={questionId ? `q-${questionId}` : null}
          onChange={handleTextChange}
          placeholder={showReadOnlyPlaceholder ? 'CLICK TO SIGN' : ''}
          style={{
            height: 212,
            fontFamily: 'cursive',
            paddingBottom: 0,
            paddingLeft: 20,
            paddingTop: 175,
            position: 'relative',
            lineHeight: 'unset',
            textAlign: 'left',
            pointerEvents:
              readonly || displayOnly || isConfirmed || showPad
                ? 'none'
                : 'auto',
          }}
          value={localText}
          willShowNotAllowedCursor={showNotAllowedCursor}
        />
      </div>
      <span className="absolute bottom-6 left-6 select-none text-base text-black">
        X
      </span>
      {(forceClearButton || (isConfirmed && !readonly)) && (
        <Button
          className="absolute right-1 top-1 h-8 px-2 text-sm !text-red-500 !outline-none hover:!bg-red-500/10"
          onClick={onResetHandler}
          type="link"
        >
          Clear
        </Button>
      )}
    </div>
  )

  const confirmDisabled = React.useMemo(
    () => (showPad ? !localImage : !localText),
    [localImage, localText, showPad]
  )

  const initialAll = React.useCallback(() => {
    if (!initials) return

    if (onInitialAll) onInitialAll(answerToSave)
  }, [answerToSave, initials, onInitialAll])

  const showInitialAll = React.useMemo(() => {
    if (hideInitialAll) return false
    if (!teamConfig?.initial_all) return false

    return initials && !displayOnly && !isConfirmed
  }, [
    displayOnly,
    hideInitialAll,
    initials,
    isConfirmed,
    teamConfig?.initial_all,
  ])

  const showConsentText = React.useMemo(() => {
    if (hideConsentText) return false

    if (showConsent) return true

    return !isConfirmed && !displayOnly
  }, [displayOnly, hideConsentText, isConfirmed, showConsent])

  const showConfirmButton = !readonly && !displayOnly && !hideConfirmButton

  const onReadOnlyClickAction = () => {
    if (!readonly || !readOnlyClickAction) return
    readOnlyClickAction()
  }

  React.useEffect(() => {
    if (showModal || isVisible) {
      document.body.style.overflow = 'hidden'
      document.body.style.touchAction = 'none'
    } else {
      document.body.style.overflow = 'auto'
      document.body.style.touchAction = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
      document.body.style.touchAction = 'auto'
    }
  }, [showModal, isVisible])

  return (
    <div>
      <Space size="small">
        {!hideLabel && (
          <>
            {label ? (
              <QuestionTitle isOfficeUse={isOfficeUse} isRequired={isRequired}>
                {label}
              </QuestionTitle>
            ) : (
              <Text $large $semibold>
                {showPad ? 'Draw' : 'Type'} Your{' '}
                {initials ? 'Initials' : 'Signature'}
              </Text>
            )}
          </>
        )}
        {!isConfirmed && !noModal ? (
          <div className="flex flex-col">
            <button
              className="relative flex h-60 w-full items-center justify-center rounded border-2 border-dashed !border-gray-200/50 bg-gray-100/50 text-gray-500 !outline-none hover:bg-gray-50"
              data-cy={questionId ? `click-to-sign-q-${questionId}` : null}
              onClick={() => {
                setShowModal(true)
                setShowPad(isDraw)
              }}
            >
              <span className="absolute left-2 top-2 flex items-center justify-center gap-1 text-xs">
                <SignatureIcon className="text-lg" /> Signature
              </span>
              Click here to sign
            </button>
            {showConsentText && (
              <span className="mt-1 w-full text-left text-xs italic text-gray-500">
                I understand this is a legal representation of my{' '}
                {initials ? 'initials' : 'signature'}
              </span>
            )}
            {hasError && (
              <div>
                <ErrorLabel>{errorMessage}</ErrorLabel>
              </div>
            )}
          </div>
        ) : null}
        <div
          className={cn(
            'hidden h-full w-full !p-0',
            { flex: isConfirmed || noModal },
            {
              'fixed left-0 top-0 z-[9999999] m-0 flex h-full w-full items-center justify-center bg-black/35 p-4 backdrop-blur-sm':
                showModal,
            }
          )}
        >
          <div
            className={cn(
              'flex h-fit w-full max-w-xl flex-col',
              {
                'rounded bg-white px-3 py-6 max-lg:h-full max-lg:!max-w-full':
                  !isConfirmed && !noModal,
              },
              { 'max-w-full': noModal }
            )}
          >
            {!shouldDisplayTypeControls && !isConfirmed ? (
              <span className="mb-2 text-xl font-bold text-gray-900">
                {modalTitle}
              </span>
            ) : null}
            {shouldDisplayTypeControls && !isConfirmed ? (
              <ButtonGroup.Root className="mb-2 w-[fit-content]">
                <ButtonGroup.Item
                  className={cn('!h-auto !rounded-md !px-5 py-1', {
                    'text-black': tabOption === 'text',
                  })}
                  isActive={tabOption === 'text'}
                  onClick={() => onHandleTab('text')}
                >
                  <TextT className="text-lg" /> Text
                </ButtonGroup.Item>
                <ButtonGroup.Item
                  className={cn('!h-auto !rounded-md !px-5 py-1', {
                    'text-black': tabOption === 'draw',
                  })}
                  isActive={tabOption === 'draw'}
                  onClick={() => onHandleTab('draw')}
                >
                  <SignatureIcon className="text-lg" /> Draw
                </ButtonGroup.Item>
              </ButtonGroup.Root>
            ) : null}

            <div onClick={onReadOnlyClickAction}>
              {showPad ? SignaturePad : SingleLine}
            </div>

            <Flex justify={showConsentText ? 'between' : 'end'}>
              {showConsentText && (
                <span className="mt-1 w-full text-left text-xs italic text-gray-500">
                  I understand this is a legal representation of my{' '}
                  {initials ? 'initials' : 'signature'}
                </span>
              )}
              {isConfirmed && (
                <div className="mt-1 flex items-center text-sm text-txt">
                  <Icons.CheckCircle className="mr-1" color="text-green-500" />
                  {getSignedOnDateMessage(answer)}
                </div>
              )}
            </Flex>
            <div>
              {showInitialAll && (
                <PrimaryButton
                  className="mr-2"
                  disabled={confirmDisabled || isConfirmed}
                  loading={isConfirming}
                  onClick={initialAll}
                >
                  Initial All
                </PrimaryButton>
              )}

              {showModal || noModal ? (
                <div className="mt-3 flex w-full justify-end gap-3">
                  <Button
                    disabled={isConfirming}
                    onClick={() => {
                      onCancel?.()
                      setShowModal(false)
                    }}
                  >
                    Cancel
                  </Button>
                  {showConfirmButton && (
                    <Button
                      data-cy={questionId ? `confirm-q-${questionId}` : null}
                      disabled={confirmDisabled || isConfirmed}
                      loading={isConfirming}
                      onClick={onConfirmHandler}
                      type="primary"
                    >
                      {initials ? 'Confirm' : 'Confirm Signature'}
                    </Button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </Space>
    </div>
  )
}

export default SignatureQuestionRender
